import { Plus, Save, Upload } from "lucide-react";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import type { FontAssetRow, FontSetRow } from "@/components/settings/settings-types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPatch, apiPost } from "@/lib/client/api";

type FontsResponse = {
  fonts: FontAssetRow[];
  fontSets: FontSetRow[];
};

type FontAssetForm = {
  fileId: string;
  name: string;
  familyName: string;
  languageScope: "en" | "zh" | "both" | "numeric";
  weight: number;
  style: string;
  licenseNote: string;
  active: boolean;
};

type FontSetForm = {
  name: string;
  titleFontId: string;
  bodyFontId: string;
  englishFontId: string;
  chineseFontId: string;
  numericFontId: string;
};

type UploadResponse = {
  id: string;
  licenseWarning: string | null;
};

const emptyAssetForm: FontAssetForm = {
  fileId: "",
  name: "",
  familyName: "",
  languageScope: "both",
  weight: 400,
  style: "normal",
  licenseNote: "",
  active: true,
};

const emptySetForm: FontSetForm = {
  name: "",
  titleFontId: "",
  bodyFontId: "",
  englishFontId: "",
  chineseFontId: "",
  numericFontId: "",
};

export function FontsSettings({ refreshToken = 0 }: { refreshToken?: number }) {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const [fonts, setFonts] = useState<FontAssetRow[]>([]);
  const [fontSets, setFontSets] = useState<FontSetRow[]>([]);
  const [assetDrafts, setAssetDrafts] = useState<Record<string, FontAssetForm>>({});
  const [setDrafts, setSetDrafts] = useState<Record<string, FontSetForm>>({});
  const [assetForm, setAssetForm] = useState<FontAssetForm>(emptyAssetForm);
  const [setForm, setSetForm] = useState<FontSetForm>(emptySetForm);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadFonts() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<FontsResponse>("/api/settings/fonts");
      setFonts(response.fonts);
      setFontSets(response.fontSets);
      setAssetDrafts(
        Object.fromEntries(
          response.fonts.map((font) => [
            font.id,
            {
              fileId: font.fileId ?? "",
              name: font.name,
              familyName: font.familyName,
              languageScope: font.languageScope,
              weight: font.weight,
              style: font.style,
              licenseNote: font.licenseNote ?? "",
              active: font.active,
            },
          ]),
        ),
      );
      setSetDrafts(
        Object.fromEntries(
          response.fontSets.map((fontSet) => [
            fontSet.id,
            {
              name: fontSet.name,
              titleFontId: fontSet.titleFontId ?? "",
              bodyFontId: fontSet.bodyFontId ?? "",
              englishFontId: fontSet.englishFontId ?? "",
              chineseFontId: fontSet.chineseFontId ?? "",
              numericFontId: fontSet.numericFontId ?? "",
            },
          ]),
        ),
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadFonts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, refreshToken]);

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) return;

    setUploading(true);
    setError(null);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.set("file", file);
      formData.set("purpose", "font");

      const response = await fetch("/api/files/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const payload = (await response.json()) as UploadResponse | { error?: string };

      if (!response.ok || !("id" in payload)) {
        throw new Error("error" in payload && payload.error ? payload.error : text.uploadError);
      }

      setAssetForm((current) => ({
        ...current,
        fileId: payload.id,
        name: current.name || file.name.replace(/\.[^.]+$/, ""),
        familyName: current.familyName || file.name.replace(/\.[^.]+$/, ""),
        licenseNote: payload.licenseWarning ?? current.licenseNote,
      }));
      setMessage(text.uploaded);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : text.uploadError);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function createAsset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("asset:new");
    setError(null);
    setMessage(null);

    try {
      await apiPost<FontAssetPayload, { id: string }>("/api/settings/fonts/assets", assetPayload(assetForm));
      setAssetForm(emptyAssetForm);
      setMessage(text.assetCreated);
      await loadFonts();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function saveAsset(id: string) {
    const draft = assetDrafts[id];

    if (!draft) return;

    setSavingKey(`asset:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<FontAssetPayload, { ok: true }>(`/api/settings/fonts/assets/${id}`, assetPayload(draft));
      setMessage(text.assetSaved);
      await loadFonts();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function createSet(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingKey("set:new");
    setError(null);
    setMessage(null);

    try {
      await apiPost<FontSetPayload, { id: string }>("/api/settings/fonts/sets", setPayload(setForm));
      setSetForm(emptySetForm);
      setMessage(text.setCreated);
      await loadFonts();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  async function saveSet(id: string) {
    const draft = setDrafts[id];

    if (!draft) return;

    setSavingKey(`set:${id}`);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<FontSetPayload, { ok: true }>(`/api/settings/fonts/sets/${id}`, setPayload(draft));
      setMessage(text.setSaved);
      await loadFonts();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">{text.title}</h1>
        <p className="text-sm text-muted-foreground">{text.description}</p>
      </header>

      <StatusMessage message={message} error={error} />

      {loading ? <Card className="p-4 text-sm text-muted-foreground">{text.loading}</Card> : null}
      {!loading ? (
        <>
          <Card className="p-4">
            <form className="space-y-3" onSubmit={createAsset}>
              <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_110px_100px_100px_auto]">
                <Field label={text.name} value={assetForm.name} onChange={(value) => setAssetForm({ ...assetForm, name: value })} />
                <Field label={text.familyName} value={assetForm.familyName} onChange={(value) => setAssetForm({ ...assetForm, familyName: value })} />
                <Field label={text.fileId} value={assetForm.fileId} onChange={(value) => setAssetForm({ ...assetForm, fileId: value })} />
                <ScopeField value={assetForm.languageScope} onChange={(value) => setAssetForm({ ...assetForm, languageScope: value })} text={text} />
                <NumberField label={text.weight} value={assetForm.weight} onChange={(value) => setAssetForm({ ...assetForm, weight: value })} />
                <ToggleField label={text.active} checked={assetForm.active} onChange={(checked) => setAssetForm({ ...assetForm, active: checked })} />
                <Button type="submit" className="mt-5" disabled={savingKey === "asset:new"}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {text.createAsset}
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <Textarea value={assetForm.licenseNote} placeholder={text.licenseNote} onChange={(event) => setAssetForm({ ...assetForm, licenseNote: event.target.value })} />
                <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-border px-3 text-sm font-medium transition hover:bg-muted">
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  {uploading ? text.uploading : text.upload}
                  <input type="file" accept=".ttf,.otf,.woff,.woff2,font/*" className="hidden" onChange={(event) => void handleUpload(event)} disabled={uploading} />
                </label>
              </div>
            </form>
          </Card>

          <Card className="p-4">
            <form className="space-y-3" onSubmit={createSet}>
              <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]">
                <Field label={text.setName} value={setForm.name} onChange={(value) => setSetForm({ ...setForm, name: value })} />
                <FontSelect label={text.titleFont} fonts={fonts} value={setForm.titleFontId} onChange={(value) => setSetForm({ ...setForm, titleFontId: value })} />
                <FontSelect label={text.bodyFont} fonts={fonts} value={setForm.bodyFontId} onChange={(value) => setSetForm({ ...setForm, bodyFontId: value })} />
                <FontSelect label={text.englishFont} fonts={fonts} value={setForm.englishFontId} onChange={(value) => setSetForm({ ...setForm, englishFontId: value })} />
                <FontSelect label={text.chineseFont} fonts={fonts} value={setForm.chineseFontId} onChange={(value) => setSetForm({ ...setForm, chineseFontId: value })} />
                <FontSelect label={text.numericFont} fonts={fonts} value={setForm.numericFontId} onChange={(value) => setSetForm({ ...setForm, numericFontId: value })} />
                <Button type="submit" className="mt-5" disabled={savingKey === "set:new"}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {text.createSet}
                </Button>
              </div>
            </form>
          </Card>

          <div className="space-y-3">
            <h2 className="text-sm font-medium">{text.assets}</h2>
            {fonts.map((font) => {
              const draft = assetDrafts[font.id];

              if (!draft) return null;

              return (
                <Card key={font.id} className="p-4">
                  <div className="grid gap-3 lg:grid-cols-[1fr_1fr_120px_110px_100px_100px_auto]">
                    <Field label={text.name} value={draft.name} onChange={(value) => updateAssetDraft(font.id, { name: value })} />
                    <Field label={text.familyName} value={draft.familyName} onChange={(value) => updateAssetDraft(font.id, { familyName: value })} />
                    <ScopeField value={draft.languageScope} onChange={(value) => updateAssetDraft(font.id, { languageScope: value })} text={text} />
                    <NumberField label={text.weight} value={draft.weight} onChange={(value) => updateAssetDraft(font.id, { weight: value })} />
                    <Field label={text.style} value={draft.style} onChange={(value) => updateAssetDraft(font.id, { style: value })} />
                    <ToggleField label={text.active} checked={draft.active} onChange={(checked) => updateAssetDraft(font.id, { active: checked })} />
                    <Button type="button" variant="secondary" className="mt-5" onClick={() => void saveAsset(font.id)} disabled={savingKey === `asset:${font.id}`}>
                      <Save className="h-4 w-4" aria-hidden="true" />
                      {text.save}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-medium">{text.sets}</h2>
            {fontSets.map((fontSet) => {
              const draft = setDrafts[fontSet.id];

              if (!draft) return null;

              return (
                <Card key={fontSet.id} className="p-4">
                  <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]">
                    <Field label={text.setName} value={draft.name} onChange={(value) => updateSetDraft(fontSet.id, { name: value })} />
                    <FontSelect label={text.titleFont} fonts={fonts} value={draft.titleFontId} onChange={(value) => updateSetDraft(fontSet.id, { titleFontId: value })} />
                    <FontSelect label={text.bodyFont} fonts={fonts} value={draft.bodyFontId} onChange={(value) => updateSetDraft(fontSet.id, { bodyFontId: value })} />
                    <FontSelect label={text.englishFont} fonts={fonts} value={draft.englishFontId} onChange={(value) => updateSetDraft(fontSet.id, { englishFontId: value })} />
                    <FontSelect label={text.chineseFont} fonts={fonts} value={draft.chineseFontId} onChange={(value) => updateSetDraft(fontSet.id, { chineseFontId: value })} />
                    <FontSelect label={text.numericFont} fonts={fonts} value={draft.numericFontId} onChange={(value) => updateSetDraft(fontSet.id, { numericFontId: value })} />
                    <Button type="button" variant="secondary" className="mt-5" onClick={() => void saveSet(fontSet.id)} disabled={savingKey === `set:${fontSet.id}`}>
                      <Save className="h-4 w-4" aria-hidden="true" />
                      {text.save}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );

  function updateAssetDraft(id: string, patch: Partial<FontAssetForm>) {
    setAssetDrafts((current) => ({ ...current, [id]: { ...(current[id] ?? emptyAssetForm), ...patch } }));
  }

  function updateSetDraft(id: string, patch: Partial<FontSetForm>) {
    setSetDrafts((current) => ({ ...current, [id]: { ...(current[id] ?? emptySetForm), ...patch } }));
  }
}

type FontAssetPayload = {
  fileId: string | null;
  name: string;
  familyName: string;
  languageScope: FontAssetForm["languageScope"];
  weight: number;
  style: string;
  licenseNote: string | null;
  active: boolean;
};

type FontSetPayload = {
  name: string;
  titleFontId: string | null;
  bodyFontId: string | null;
  englishFontId: string | null;
  chineseFontId: string | null;
  numericFontId: string | null;
};

function assetPayload(form: FontAssetForm): FontAssetPayload {
  return {
    fileId: emptyToNull(form.fileId),
    name: form.name.trim(),
    familyName: form.familyName.trim(),
    languageScope: form.languageScope,
    weight: Number(form.weight),
    style: form.style.trim() || "normal",
    licenseNote: emptyToNull(form.licenseNote),
    active: form.active,
  };
}

function setPayload(form: FontSetForm): FontSetPayload {
  return {
    name: form.name.trim(),
    titleFontId: emptyToNull(form.titleFontId),
    bodyFontId: emptyToNull(form.bodyFontId),
    englishFontId: emptyToNull(form.englishFontId),
    chineseFontId: emptyToNull(form.chineseFontId),
    numericFontId: emptyToNull(form.numericFontId),
  };
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="mt-5 flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-primary" />
      {label}
    </label>
  );
}

function ScopeField({
  text,
  value,
  onChange,
}: {
  text: typeof en;
  value: FontAssetForm["languageScope"];
  onChange: (value: FontAssetForm["languageScope"]) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.scope}</span>
      <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={value} onChange={(event) => onChange(event.target.value as FontAssetForm["languageScope"])}>
        <option value="both">{text.both}</option>
        <option value="en">{text.english}</option>
        <option value="zh">{text.chinese}</option>
        <option value="numeric">{text.numeric}</option>
      </select>
    </label>
  );
}

function FontSelect({ label, fonts, value, onChange }: { label: string; fonts: FontAssetRow[]; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">-</option>
        {fonts.map((font) => (
          <option key={font.id} value={font.id}>
            {font.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusMessage({ message, error }: { message: string | null; error: string | null }) {
  if (!message && !error) return null;

  return (
    <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
      {error ?? message}
    </div>
  );
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

const en = {
  title: "Fonts",
  description: "Upload licensed font assets and build font sets used by quotation exports.",
  name: "Name",
  familyName: "Family",
  fileId: "File ID",
  scope: "Scope",
  both: "Both",
  english: "English",
  chinese: "Chinese",
  numeric: "Numeric",
  weight: "Weight",
  style: "Style",
  active: "Active",
  licenseNote: "License note",
  upload: "Upload font",
  uploading: "Uploading...",
  createAsset: "Create asset",
  setName: "Set name",
  titleFont: "Title",
  bodyFont: "Body",
  englishFont: "English",
  chineseFont: "Chinese",
  numericFont: "Numeric",
  createSet: "Create set",
  assets: "Font assets",
  sets: "Font sets",
  save: "Save",
  loading: "Loading fonts...",
  uploaded: "Font uploaded and attached to the asset form.",
  assetCreated: "Font asset created.",
  assetSaved: "Font asset saved.",
  setCreated: "Font set created.",
  setSaved: "Font set saved.",
  loadError: "Unable to load fonts.",
  saveError: "Unable to save fonts.",
  uploadError: "Unable to upload font.",
};

const zh: typeof en = {
  title: "字体",
  description: "上传已授权字体资产，并配置报价导出使用的字体组合。",
  name: "名称",
  familyName: "字体族",
  fileId: "文件 ID",
  scope: "范围",
  both: "双语",
  english: "英文",
  chinese: "中文",
  numeric: "数字",
  weight: "字重",
  style: "样式",
  active: "启用",
  licenseNote: "授权备注",
  upload: "上传字体",
  uploading: "上传中...",
  createAsset: "创建字体",
  setName: "组合名称",
  titleFont: "标题",
  bodyFont: "正文",
  englishFont: "英文",
  chineseFont: "中文",
  numericFont: "数字",
  createSet: "创建组合",
  assets: "字体资产",
  sets: "字体组合",
  save: "保存",
  loading: "正在加载字体...",
  uploaded: "字体已上传并填入表单。",
  assetCreated: "字体资产已创建。",
  assetSaved: "字体资产已保存。",
  setCreated: "字体组合已创建。",
  setSaved: "字体组合已保存。",
  loadError: "无法加载字体。",
  saveError: "无法保存字体。",
  uploadError: "无法上传字体。",
};
